/**
 * Contexto Global de Picture-in-Picture
 * 
 * Gerencia o estado do PIP de forma global, independente da navegação
 * Garante que a janela PIP persista mesmo ao trocar de página
 * 
 * @author VISION DESK
 * @version 0.24.2
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { PIPWindow } from '@/components/PictureInPicture/PIPWindow';
import { usePIP, getBrowserInfo } from '@/hooks/usePIP';
import { getContextInfo } from '@/utils/secureContext';
import toast from 'react-hot-toast';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface PIPState {
  osId: string;
  sessionId: string;
  osTitle?: string;
}

interface PIPContextData {
  /**
   * Se PIP está aberto
   */
  isPIPOpen: boolean;
  
  /**
   * Janela PIP (se modo nativo)
   */
  pipWindow: Window | null;
  
  /**
   * Se navegador suporta PIP nativo
   */
  isPIPSupported: boolean;
  
  /**
   * Se está usando modo fallback (modal)
   */
  isFallbackMode: boolean;
  
  /**
   * Estado atual do PIP
   */
  pipState: PIPState | null;
  
  /**
   * Abrir PIP para uma OS específica
   */
  openPIP: (osId: string, sessionId: string, osTitle?: string) => Promise<void>;
  
  /**
   * Fechar PIP
   */
  closePIP: () => void;
  
  /**
   * Atualizar dados do PIP (quando sessão muda)
   */
  updatePIP: (osId: string, sessionId: string, osTitle?: string) => void;
}

// ============================================================================
// CONTEXTO
// ============================================================================

const PIPContext = createContext<PIPContextData | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export const PIPProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pipState, setPIPState] = useState<PIPState | null>(null);
  const isOpeningRef = useRef(false);

  // Hook usePIP (será inicializado quando PIP for aberto)
  const { 
    isPIPOpen, 
    pipWindow, 
    isPIPSupported, 
    openPIP: openPIPNative, 
    closePIP: closePIPNative,
    isFallbackMode 
  } = usePIP({
    osId: pipState?.osId || '',
    sessionId: pipState?.sessionId || '',
    onOpen: () => {
      // console.log('✅ PIP Context: PIP aberto com sucesso');
      isOpeningRef.current = false;
    },
    onClose: () => {
      // console.log('👋 PIP Context: PIP fechado');
      setPIPState(null);
      localStorage.removeItem('pip-state');
      isOpeningRef.current = false;
    },
  });

  /**
   * Abrir PIP para uma OS específica
   */
  const openPIP = useCallback(async (osId: string, sessionId: string, osTitle?: string) => {
    // Prevenir múltiplas aberturas simultâneas
    if (isOpeningRef.current) {
      // console.log('⚠️ PIP Context: Já está abrindo PIP, ignorando...');
      return;
    }

    if (isPIPOpen) {
      // console.log('⚠️ PIP Context: PIP já está aberto, atualizando dados...');
      setPIPState({ osId, sessionId, osTitle });
      return;
    }

    try {
      isOpeningRef.current = true;
      
      // console.log('🚀 PIP Context: Iniciando abertura do PIP...', { osId, sessionId, osTitle });
      
      // Atualizar estado
      setPIPState({ osId, sessionId, osTitle });
      
      // Salvar no localStorage para persistência
      localStorage.setItem('pip-state', JSON.stringify({ osId, sessionId, osTitle, timestamp: Date.now() }));
      
      // Verificar navegador e contexto
      const browserInfo = getBrowserInfo();
      const contextInfo = getContextInfo();
      
      // Avisar sobre fallback com mensagem específica
      if (!browserInfo.supportsNativePIP) {
        let message = '⚠️ PIP nativo não disponível. Usando modal flutuante.';
        
        // Mensagem específica se está acessando via IP local
        if (contextInfo.isLocalNetworkIP && !contextInfo.isSecureContext) {
          message = `⚠️ Acesso via IP (${contextInfo.hostname}). PIP requer HTTPS ou localhost. Usando modal flutuante.`;
        }
        
        toast(message, { 
          duration: 4000,
          icon: 'ℹ️',
          style: {
            background: '#3b82f6',
            color: '#fff'
          }
        });
      }

      // Abrir PIP (nativo ou fallback)
      await openPIPNative();
      
      // console.log('✅ PIP Context: PIP aberto com sucesso');
    } catch (error) {
      // console.error('❌ PIP Context: Erro ao abrir PIP:', error);
      isOpeningRef.current = false;
      toast.error('Erro ao abrir Picture-in-Picture');
    }
  }, [isPIPOpen, openPIPNative]);

  /**
   * Fechar PIP
   */
  const closePIP = useCallback(() => {
    // console.log('👋 PIP Context: Fechando PIP...');
    closePIPNative();
    setPIPState(null);
    localStorage.removeItem('pip-state');
    isOpeningRef.current = false;
  }, [closePIPNative]);

  /**
   * Atualizar dados do PIP (quando sessão muda)
   */
  const updatePIP = useCallback((osId: string, sessionId: string, osTitle?: string) => {
    if (!isPIPOpen) {
      // console.log('⚠️ PIP Context: PIP não está aberto, não é possível atualizar');
      return;
    }

    // console.log('🔄 PIP Context: Atualizando dados do PIP...', { osId, sessionId, osTitle });
    setPIPState({ osId, sessionId, osTitle });
    
    // Atualizar localStorage
    localStorage.setItem('pip-state', JSON.stringify({ osId, sessionId, osTitle, timestamp: Date.now() }));
  }, [isPIPOpen]);

  /**
   * Recuperar estado do PIP ao recarregar página
   */
  useEffect(() => {
    const savedState = localStorage.getItem('pip-state');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        const timeDiff = Date.now() - state.timestamp;
        
        // Se foi salvo há menos de 5 minutos, recuperar
        if (timeDiff < 5 * 60 * 1000) {
          // console.log('🔄 PIP Context: Estado recuperado do localStorage', state);
          setPIPState({
            osId: state.osId,
            sessionId: state.sessionId,
            osTitle: state.osTitle,
          });
        } else {
          localStorage.removeItem('pip-state');
        }
      } catch (error) {
        // console.error('❌ PIP Context: Erro ao recuperar estado:', error);
        localStorage.removeItem('pip-state');
      }
    }
  }, []);

  // Valor do contexto
  const value: PIPContextData = {
    isPIPOpen,
    pipWindow,
    isPIPSupported,
    isFallbackMode,
    pipState,
    openPIP,
    closePIP,
    updatePIP,
  };

  return (
    <PIPContext.Provider value={value}>
      {children}
      
      {/* Renderizar PIPWindow globalmente quando aberto */}
      {pipState && (isPIPOpen || isFallbackMode) && (
        <PIPWindow
          osId={pipState.osId}
          sessionId={pipState.sessionId}
          pipWindow={pipWindow}
          onClose={closePIP}
          isFallbackMode={isFallbackMode}
        />
      )}
    </PIPContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook para acessar o contexto de PIP
 */
export const usePIPContext = () => {
  const context = useContext(PIPContext);
  
  if (!context) {
    throw new Error('usePIPContext deve ser usado dentro de PIPProvider');
  }
  
  return context;
};
